/**
 * /admin — back office dashboard shell.
 *
 * Passphrase-gated. On success the passphrase is stashed in localStorage
 * (via secureStorage) so subsequent visits skip the unlock step.
 *
 * This file wires data fetching + action handlers; the visual tiles live
 * in /components/admin/*.jsx.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCcw, LogOut, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  fetchAdminStats,
  fetchAdminSubmissions,
  updateAdminSubmission,
  fetchAdminWaitlist,
  fetchAdminMedLibrary,
  upsertAdminMedLibrary,
  deleteAdminMedLibrary,
  fetchAdminMedFeedback,
  updateAdminMedFeedback,
  fetchAdminMilestones,
  adminResendMilestone,
} from "@/lib/api";
import { secureGet, secureSet, secureRemove } from "@/lib/secureStorage";
import { errorMessage } from "@/lib/errorMessage";
import useSEO from "@/lib/useSEO";

import Unlock from "@/components/admin/Unlock";
import PlansRunPanel from "@/components/admin/PlansRunPanel";
import RevenuePanel from "@/components/admin/RevenuePanel";
import CommunityPanel from "@/components/admin/CommunityPanel";
import ListingsPanel from "@/components/admin/ListingsPanel";
import SponsorsPanel from "@/components/admin/SponsorsPanel";
import WaitlistPanel from "@/components/admin/WaitlistPanel";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import MedLibraryPanel from "@/components/admin/MedLibraryPanel";
import MedFeedbackPanel from "@/components/admin/MedFeedbackPanel";
import MilestonesPanel from "@/components/admin/MilestonesPanel";
import EndpointUsagePanel from "@/components/admin/EndpointUsagePanel";
import WebhooksPanel from "@/components/admin/WebhooksPanel";
import ExternalDashboardsPanel from "@/components/admin/ExternalDashboardsPanel";
import SocialManagerCard from "@/components/admin/SocialManagerCard";

const PASS_KEY = "centennial_admin_pass_v1";

export default function Admin() {
  useSEO({
    title: "Admin · Centennial Back Office",
    description: "Internal admin dashboard for Centennial. Requires admin passphrase.",
    canonicalPath: "/admin",
  });

  const [pass, setPass] = useState(() => secureGet(PASS_KEY) || null);
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [medLib, setMedLib] = useState(null);
  const [medFeedback, setMedFeedback] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (p) => {
    setLoading(true);
    setErr(null);
    try {
      const [stats, submissions, wl, lib, fb, ms] = await Promise.all([
        fetchAdminStats(p),
        fetchAdminSubmissions(p).catch(() => ({ submissions: [], counts: {} })),
        fetchAdminWaitlist(p).catch(() => ({ waitlist: [], count: 0 })),
        fetchAdminMedLibrary(p).catch(() => ({ seed_count: 0, custom_count: 0, custom: [] })),
        fetchAdminMedFeedback(p).catch(() => ({ items: [], counts: {} })),
        fetchAdminMilestones(p).catch(() => ({ items: [], counts: { "60": 0, "80": 0 } })),
      ]);
      setData(stats);
      setSubs(submissions);
      setWaitlist(wl);
      setMedLib(lib);
      setMedFeedback(fb);
      setMilestones(ms);
    } catch (e) {
      const msg = errorMessage(e, "Failed to load stats.");
      setErr(msg);
      if (e?.response?.status === 401) {
        secureRemove(PASS_KEY);
        setPass(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const reloadMedLib = async () => {
    try {
      const lib = await fetchAdminMedLibrary(pass);
      setMedLib(lib);
    } catch (e) {
      toast.error(errorMessage(e, "Reload failed."));
    }
  };

  const saveMedLibEntry = async (entry) => {
    try {
      await upsertAdminMedLibrary(pass, entry);
      toast.success(`Saved ${entry.display || entry.generic}.`);
      await reloadMedLib();
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Save failed."));
      return false;
    }
  };

  const removeMedLibEntry = async (slug, name) => {
    if (!window.confirm(`Remove ${name} from the library?`)) return;
    try {
      await deleteAdminMedLibrary(pass, slug);
      toast.success("Removed.");
      await reloadMedLib();
    } catch (e) {
      toast.error(errorMessage(e, "Delete failed."));
    }
  };

  const changeFeedbackStatus = async (id, status) => {
    try {
      await updateAdminMedFeedback(pass, id, status);
      const fb = await fetchAdminMedFeedback(pass);
      setMedFeedback(fb);
    } catch (e) {
      toast.error(errorMessage(e, "Update failed."));
    }
  };

  const resendMilestone = async (email, milestone) => {
    try {
      const r = await adminResendMilestone(pass, email, milestone);
      if (r.ok) toast.success(`Resent to ${email}.`);
      else if (r.skipped) toast.error("Skipped — RESEND_API_KEY not set in the backend env.");
      else toast.error(r.error || "Send failed.");
      const ms = await fetchAdminMilestones(pass);
      setMilestones(ms);
    } catch (e) {
      toast.error(errorMessage(e, "Resend failed."));
    }
  };

  const changeSubStatus = async (sub, status) => {
    try {
      await updateAdminSubmission(pass, sub.submission_id, { status, admin_note: "" });
      toast.success(`Marked ${sub.title} as ${status}.`);
      const fresh = await fetchAdminSubmissions(pass);
      setSubs(fresh);
    } catch (e) {
      toast.error(errorMessage(e, "Update failed."));
    }
  };

  useEffect(() => {
    if (pass) load(pass);
  }, [pass]);

  const signOut = () => {
    secureRemove(PASS_KEY);
    setPass(null);
    setData(null);
  };

  if (!pass) return <Unlock passKey={PASS_KEY} onUnlocked={(p) => setPass(p)} />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] px-6 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Top nav */}
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
          >
            <ArrowLeft size={14} /> Back to calculator
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => load(pass)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs text-[#5C6B62] hover:text-[#2B5339] disabled:opacity-50"
            >
              <RefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-signout"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-xs text-[#5C6B62] hover:text-[#C26747]"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        <h1 className="text-3xl font-medium text-[#1C2B23] mb-1">Back office</h1>
        <p className="text-sm text-[#5C6B62] mb-8">
          Live from your production database. Data refreshes on demand — no caching.
        </p>

        {err && (
          <div
            data-testid="admin-error"
            className="bg-[#FDF2ED] border border-[#F5C7B4] text-[#C26747] rounded-xl p-4 text-sm mb-6"
          >
            {err}
          </div>
        )}

        {!data && loading && (
          <div className="flex items-center gap-2 text-sm text-[#5C6B62]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <PlansRunPanel plans={data.plans_run} />
            <RevenuePanel revenue={data.revenue} />
            <CommunityPanel scenariosSaved={data.scenarios_saved} />
            <ListingsPanel listings={data.active_listings} />
            <SponsorsPanel sponsors={data.active_sponsors} />
            <WaitlistPanel waitlist={waitlist} />
            <SubmissionsPanel subs={subs} onChangeStatus={changeSubStatus} />
            <MedLibraryPanel
              data={medLib}
              onSave={saveMedLibEntry}
              onDelete={removeMedLibEntry}
            />
            <MedFeedbackPanel
              medFeedback={medFeedback}
              onChangeStatus={changeFeedbackStatus}
            />
            <MilestonesPanel milestones={milestones} onResend={resendMilestone} />
            <EndpointUsagePanel usage={data.usage_by_endpoint} />
            <WebhooksPanel webhooks={data.recent_webhooks} />
            <SocialManagerCard />
            <ExternalDashboardsPanel />
          </div>
        )}

        <div className="mt-10 text-[11px] text-[#5C6B62]">
          Generated {data ? new Date(data.generated_at).toLocaleString() : "—"}
        </div>
      </div>
    </div>
  );
}
